import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/PasswordInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';

export default function Signup() {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSubmitting(true);
    const { data, error } = await signUp(email.trim(), password, displayName.trim());
    if (error) {
      setSubmitting(false);
      toast.error(error.message || 'Unable to sign up');
      return;
    }
    // Fire-and-forget ClickFunnels sync (lead magnet funnel). Never blocks signup.
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      if (apiUrl) {
        fetch(`${apiUrl}/api/clickfunnels/sync-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email.trim(),
            display_name: displayName.trim() || undefined,
          }),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (err) {
      // Never block signup on CF sync — surface for debugging only.
      console.warn('[mfh] CF sync failed:', err);
    }
    // If email confirmation is OFF (Supabase default for new projects often ON), session may exist
    if (data?.session) {
      toast.success('Account created');
      navigate('/', { replace: true });
    } else {
      // Try auto-login (works if confirmations are disabled)
      const { error: loginErr } = await signIn(email.trim(), password);
      if (!loginErr) {
        toast.success('Welcome to My Flow Habit');
        navigate('/', { replace: true });
      } else {
        setDone(true);
      }
    }
    setSubmitting(false);
  };

  if (done) {
    return (
      <div className="min-h-screen flex flex-col relative z-10" data-testid="signup-success">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="text-primary mb-8 flex justify-center"><Logo /></div>
          <div className="rounded-2xl border border-border bg-card p-9">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-semibold tracking-tight">Check your inbox</h1>
            <p className="text-sm text-muted-foreground mt-2">
              We sent a verification link to <span className="text-foreground font-medium">{email}</span>. Open it to activate your account.
            </p>
            <Link to="/login" className="mt-6 inline-block">
              <Button variant="ghost" className="rounded-full" data-testid="signup-success-login">Go to sign in</Button>
            </Link>
          </div>
        </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="signup-page">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-primary mb-8 flex justify-center"><Logo /></div>
        <div className="rounded-2xl border border-border bg-card p-7 sm:p-9">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Create your flow</h1>
          <p className="text-sm text-muted-foreground mt-1.5">Build calm, consistent habits.</p>
          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name">Display name</Label>
              <Input id="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Alex" data-testid="signup-name" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} testid="signup-password" autoComplete="new-password" />
              <p className="text-xs text-muted-foreground mt-1.5">At least 6 characters.</p>
            </div>
            <Button type="submit" disabled={submitting} className="w-full rounded-full mt-2" data-testid="signup-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create account
            </Button>
          </form>
          <p className="mt-6 text-sm text-center text-muted-foreground">
            Already have one? <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="signup-login-link">Sign in</Link>
          </p>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
}
